import { useTranslation } from 'react-i18next';

interface RatingStarsProps {
  /** Der Durchschnitt aus allen Bewertungen -- fuer die gefuellten Sterne. */
  average: number;
  /** Wie viele bewertet haben. Ohne diese Zahl ist ein Durchschnitt nichts. */
  count: number;
  /** Fehlt sie, stehen die Sterne nur da. */
  onRate?: (value: number) => void;
  busy?: boolean;
}

/**
 * Die Bewertung eines Artikels -- F4-14.
 *
 * **Vorher waren das fuenf Schaltflaechen mit den Ziffern 1 bis 5.** Was sie
 * bedeuten, stand nirgends; ob eine Bewertung angekommen war, auch nicht. Wer
 * eine Anleitung als unbrauchbar melden wollte, musste raten, ob die 1 oder
 * die 5 dafuer steht.
 *
 * Sterne sagen die Richtung ohne Beschriftung. Die Beschriftung bleibt
 * trotzdem an jedem Knopf: Ein Stern ist fuer ein vorlesendes Programm kein
 * Wert, und ``aria-label`` traegt hier die eigentliche Aussage.
 *
 * Der Durchschnitt steht als Zahl daneben und nicht statt der Sterne. Wer
 * Farben oder Formen nicht unterscheidet, liest "3,6 von 5 aus 12
 * Bewertungen" -- und verliert nichts ausser der schnellen Zuordnung.
 */
export function RatingStars({ average, count, onRate, busy = false }: RatingStarsProps) {
  const { t } = useTranslation();
  const gerundet = Math.round(average);

  return (
    <div className="stack" style={{ gap: 'var(--space-1)' }}>
      <div className="kb-stars" role="group" aria-label={t('knowledge.rateArticle')}>
        {[1, 2, 3, 4, 5].map((value) => {
          const gefuellt = value <= gerundet;
          const className = gefuellt ? 'kb-stars__button kb-stars__button--on' : 'kb-stars__button';

          if (!onRate) {
            return (
              <span key={value} className={className} aria-hidden="true">
                {gefuellt ? '★' : '☆'}
              </span>
            );
          }

          return (
            <button
              key={value}
              type="button"
              className={className}
              disabled={busy}
              aria-label={t('knowledge.rateWith', { value })}
              onClick={() => {
                onRate(value);
              }}
            >
              {gefuellt ? '★' : '☆'}
            </button>
          );
        })}
      </div>

      <span className="field__hint">
        {count > 0
          ? t('knowledge.ratingSummary', { average: average.toFixed(1), count })
          : t('knowledge.notRatedYet')}
      </span>
    </div>
  );
}
